import { useEffect, useRef, useState } from "react";
import { Mic, Trash2, Pause, Play, Send, Lock, ArrowUp } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";

// pick best supported MIME for MediaRecorder
const pickMime = () => {
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/ogg;codecs=opus",
    "audio/mp4",
    "audio/mpeg",
  ];
  for (const m of candidates) {
    if (window.MediaRecorder && MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(m)) return m;
  }
  return "";
};

const fmtSecs = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

const LOCK_THRESHOLD_PX = 70;

/**
 * WhatsApp-style voice recorder button.
 *
 * - Hold the mic to record; release to send.
 * - While holding, slide UP to lock the recording (hands-free).
 * - Locked mode: pause / resume / delete / send controls.
 */
export const VoiceMicWhatsApp = ({ busy, disabled, onSend, onRecordStart }) => {
  const [recState, setRecState] = useState("idle"); // idle | holding | locked | paused
  const [elapsed, setElapsed] = useState(0);
  const [slideUp, setSlideUp] = useState(0);

  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const mimeRef = useRef("audio/webm");
  const discardRef = useRef(false);
  const startYRef = useRef(0);
  const timerRef = useRef(null);
  const stateRef = useRef("idle");

  const setState = (s) => {
    stateRef.current = s;
    setRecState(s);
  };

  const startTimer = () => {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setElapsed((e) => e + 1), 1000);
  };
  const stopTimer = () => clearInterval(timerRef.current);

  const cleanupStream = () => {
    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
    streamRef.current = null;
  };

  const begin = async (clientY) => {
    if (disabled || busy || stateRef.current !== "idle") return;
    // Interrupt any ongoing AI playback BEFORE asking for the mic, so the
    // user feels the response is instantaneous (parent decides what to stop).
    try { onRecordStart && onRecordStart(); } catch { /* noop */ }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = pickMime();
      mimeRef.current = mime || "audio/webm";
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      recorderRef.current = rec;
      chunksRef.current = [];
      discardRef.current = false;
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = () => {
        cleanupStream();
        stopTimer();
        const wasDiscard = discardRef.current;
        const blob = new Blob(chunksRef.current, { type: mimeRef.current });
        setState("idle");
        setElapsed(0);
        setSlideUp(0);
        if (wasDiscard) return;
        if (blob.size < 800) {
          toast.error("Audio muy corto, intenta de nuevo.");
          return;
        }
        onSend(blob, mimeRef.current);
      };
      rec.start();
      startYRef.current = clientY;
      setElapsed(0);
      startTimer();
      setState("holding");
    } catch (e) {
      toast.error("No pude acceder al micrófono. Revisa los permisos.");
      console.error(e);
    }
  };

  // Global listeners while holding: slide-up to lock, release to send
  useEffect(() => {
    if (recState !== "holding") return undefined;
    const onMove = (ev) => {
      const y = ev.touches ? ev.touches[0]?.clientY : ev.clientY;
      if (y == null) return;
      const dy = Math.max(0, startYRef.current - y);
      setSlideUp(dy);
      if (dy > LOCK_THRESHOLD_PX) {
        setState("locked");
        setSlideUp(0);
      }
    };
    const onUp = () => {
      if (stateRef.current === "holding") stopAndSend();
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recState]);

  const stopAndSend = () => {
    discardRef.current = false;
    try {
      if (recorderRef.current && recorderRef.current.state !== "inactive") recorderRef.current.stop();
    } catch { /* noop */ }
  };

  const discard = () => {
    discardRef.current = true;
    try {
      if (recorderRef.current && recorderRef.current.state !== "inactive") recorderRef.current.stop();
    } catch { /* noop */ }
    toast.info("Grabación eliminada");
  };

  const pauseRec = () => {
    try { recorderRef.current?.pause(); } catch { /* noop */ }
    stopTimer();
    setState("paused");
  };

  const resumeRec = () => {
    try { recorderRef.current?.resume(); } catch { /* noop */ }
    startTimer();
    setState("locked");
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      discardRef.current = true;
      try {
        if (recorderRef.current && recorderRef.current.state !== "inactive") recorderRef.current.stop();
      } catch { /* noop */ }
      cleanupStream();
      stopTimer();
    };
  }, []);

  const isRecording = recState === "holding" || recState === "locked" || recState === "paused";
  const isLockedMode = recState === "locked" || recState === "paused";

  return (
    <div className="flex flex-col items-center notranslate" translate="no" data-testid="voice-mic-whatsapp">
      {/* Locked-mode control panel */}
      {isLockedMode && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-3 flex items-center gap-3 bg-black/70 border border-[#d4af37]/40 rounded-full px-4 py-2.5 backdrop-blur"
          data-testid="rec-locked-panel"
        >
          <span className="flex items-center gap-2 text-sm text-neutral-200 font-mono min-w-[64px]">
            <span
              className={`w-2.5 h-2.5 rounded-full ${recState === "paused" ? "bg-amber-400" : "bg-red-500 animate-pulse"}`}
            />
            {fmtSecs(elapsed)}
          </span>
          <button
            data-testid="rec-delete"
            onClick={discard}
            title="Eliminar grabación"
            className="w-10 h-10 rounded-full border border-red-500/40 text-red-300 hover:bg-red-500/15 flex items-center justify-center"
          >
            <Trash2 className="w-4 h-4" />
          </button>
          {recState === "paused" ? (
            <button
              data-testid="rec-resume"
              onClick={resumeRec}
              title="Reanudar"
              className="w-10 h-10 rounded-full border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/15 flex items-center justify-center"
            >
              <Play className="w-4 h-4" />
            </button>
          ) : (
            <button
              data-testid="rec-pause"
              onClick={pauseRec}
              title="Pausar"
              className="w-10 h-10 rounded-full border border-amber-500/40 text-amber-300 hover:bg-amber-500/15 flex items-center justify-center"
            >
              <Pause className="w-4 h-4" />
            </button>
          )}
          <button
            data-testid="rec-send"
            onClick={stopAndSend}
            title="Enviar"
            className="w-11 h-11 rounded-full bg-[#d4af37] text-black hover:bg-[#e6c350] flex items-center justify-center shadow-[0_0_24px_rgba(212,175,55,0.45)]"
          >
            <Send className="w-4 h-4" />
          </button>
        </motion.div>
      )}

      {/* Slide-up lock hint while holding */}
      {recState === "holding" && (
        <div className="mb-2 flex flex-col items-center" data-testid="rec-lock-hint">
          <div
            className="w-9 h-14 rounded-full border border-[#d4af37]/50 bg-black/60 flex flex-col items-center justify-center gap-1"
            style={{ transform: `translateY(-${Math.min(slideUp * 0.35, 24)}px)` }}
          >
            <Lock className="w-3.5 h-3.5 text-[#d4af37]" />
            <ArrowUp className="w-3.5 h-3.5 text-[#d4af37] animate-bounce" />
          </div>
        </div>
      )}

      {/* Main mic button */}
      <button
        data-testid={isRecording ? "btn-mic-recording" : "btn-mic-hold"}
        onPointerDown={(e) => {
          if (isLockedMode) return;
          e.preventDefault();
          begin(e.clientY);
        }}
        disabled={busy || disabled || isLockedMode}
        style={{ touchAction: "none" }}
        className={`relative w-24 h-24 rounded-full flex items-center justify-center transition-all border-2 select-none ${
          isRecording
            ? "bg-red-500/30 border-red-400 scale-110 shadow-[0_0_60px_rgba(239,68,68,0.5)]"
            : "bg-[#d4af37]/15 border-[#d4af37]/60 hover:bg-[#d4af37]/25 hover:scale-105"
        } ${busy || isLockedMode ? "opacity-50 cursor-not-allowed" : ""}`}
      >
        <Mic className={`w-10 h-10 ${isRecording ? "text-red-300" : "text-[#d4af37]"}`} />
        {isRecording && recState !== "paused" && (
          <span className="absolute inset-0 rounded-full border-2 border-red-400/60 animate-ping" />
        )}
        {recState === "holding" && (
          <span className="absolute -bottom-6 text-[10px] text-red-300 font-mono whitespace-nowrap">
            {fmtSecs(elapsed)}
          </span>
        )}
      </button>
    </div>
  );
};
